import { Bell, CheckCheck, Trash2, Trophy, Calendar, Users, Gift, Vote } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useSyndicateNotifications } from "@/hooks/useSyndicateNotifications";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { cs } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { createSlug } from "@/utils/slugs";

const notificationIcons: Record<string, typeof Bell> = {
  upcoming_race: Calendar,
  race_result: Trophy,
  syndicate_update: Bell,
  member_joined: Users,
  prize_distribution: Gift,
  owners_ballot_result: Vote,
};

export function SyndicateNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = 
    useSyndicateNotifications(user?.id);

  const handleNotificationClick = (notification: any) => {
    markAsRead.mutate(notification.id);

    // Prefer the syndicate page whenever the notification is tied to a syndicate horse
    const horseName = notification.metadata?.horse_name
      || notification.syndicates?.individual_horses?.name;

    if (horseName) {
      navigate(`/syndicates/${createSlug(horseName)}`);
      return;
    }

    if (notification.metadata?.race_id) {
      navigate(`/races/${notification.metadata.race_id}`);
    } else if (notification.metadata?.horse_id) {
      navigate(`/horses/${notification.metadata.horse_id}`);
    }
  };

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-white hover:bg-white/20 hover:text-white">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-4 py-2">
          <h3 className="font-semibold">Notifikace</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllAsRead.mutate()}
              className="h-8 px-2 text-xs"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Označit vše
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mb-2 opacity-50" />
              <p className="text-sm">Žádné notifikace</p>
            </div>
          ) : (
            notifications.map((notification) => {
              const Icon = notificationIcons[notification.notification_type] ?? Bell;
              return (
                <DropdownMenuItem
                  key={notification.id}
                  className={`flex flex-col items-start p-4 cursor-pointer focus:bg-primary/10 focus:text-foreground hover:bg-primary/10 hover:text-foreground ${
                    !notification.is_read ? "bg-complement-1/15" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className={`p-2 rounded-full ${
                      notification.notification_type === "race_result" 
                        ? "bg-complement-1/15 text-complement-1"
                        : notification.notification_type === "upcoming_race"
                        ? "bg-blue-500/10 text-blue-500"
                        : notification.notification_type === "prize_distribution"
                        ? "bg-green-500/10 text-green-500"
                        : "bg-muted"
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-sm line-clamp-2">
                          {notification.title}
                        </h4>
                        {!notification.is_read && (
                          <div className="h-2 w-2 rounded-full bg-complement-1 flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                            locale: cs,
                          })}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification.mutate(notification.id);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
